'use client';
import { useCategoryTree } from '@/hooks/useCategoryTree';

import { ParamItem } from '@/utils/SEOPathModule/convertPathToParams';
import {
  DictsGateState,
  OnChangeIsFreeType,
  OnChangePriceType,
  OnUpdateCategoriesType,
  OnUpdateParamType,
  PriceValue,
  ShownParams,
} from '../types';
import React, { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { IconX } from '@tabler/icons-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CategorySelectElem } from '../FiltersCategorySelect';
import { InputLikeSelectTrigger } from '../../Inputs/SelectInput/InputTrigger';
import { FiltersSelect } from '../FiltersSelect';
import { LocationSelector } from '../../LocationSelector';
import { useCityId, useCountryId, useDistrictId, useRegionId, useSetLocation } from '@/contexts/CookieContext';
import { GeoShow } from '../../GeoShow';
import { FiltersMinMax } from '../FiltersMinMax';
import { MobileSortSwitch } from '../../Inputs/SortSwitch/mobileSwitch';
import clsx from 'clsx';
import { FreePriceToggle } from '../FreePriceToggle';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

type Props = {
  shownParameters: ShownParams;
  params: ParamItem[];
  price: PriceValue;
  categories: string[];
  onChangePrice: OnChangePriceType;
  isFree: boolean;
  onChangeIsFree: OnChangeIsFreeType;
  onUpdateParam: OnUpdateParamType;
  onUpdateCategory: OnUpdateCategoriesType;
  onClose: () => void;
  onClear?: () => void;
  onSubmit: () => void;
  onChangeSort: (newSort: string) => void;
  sort: string;
  dictsGateState?: DictsGateState;
  showFree?: boolean;
};

export const MobileFiltersList: React.FC<Props> = ({
  onClose,
  categories,
  onUpdateCategory,
  shownParameters,
  params,
  onUpdateParam,
  price,
  onChangePrice,
  isFree,
  onChangeIsFree,
  onSubmit,
  onChangeSort,
  onClear,
  sort,
  dictsGateState = 'idle',
  showFree = false,
}) => {
  const t = useTranslations('filters');
  const tRoot = useTranslations();

  const { data: categoriesData = [], isLoading: isCategoriesLoading } = useCategoryTree();

  const [activeModalId, setActiveModalId] = useState<string>('');

  const onShowModal = useCallback((id: string) => {
    setActiveModalId(id);
  }, []);

  const onCloseModal = useCallback(() => {
    setActiveModalId('');
  }, []);

  const regionId = useRegionId();
  const cityId = useCityId();
  const districtId = useDistrictId();
  const countryId = useCountryId();

  const updateLocation = useSetLocation();

  const submitNewSearch = useCallback(() => {
    onSubmit();
    onClose();
  }, [onSubmit, onClose]);

  const isNumberParamValue = (
    value: ParamItem['value'] | undefined,
  ): value is Extract<ParamItem['value'], { from?: number; to?: number }> => {
    return value !== undefined && !Array.isArray(value) && typeof value === 'object';
  };

  return (
    <div className='relative flex h-full min-h-0 w-full flex-col bg-white'>
      <div className='flex shrink-0 items-center justify-between gap-[8px] border-b border-primary-stroke-day px-[8px] py-[10px]'>
        <div className='flex min-w-0 items-center gap-[4px]'>
          <Button
            variant='ghost'
            size='icon'
            onClick={onClose}
            aria-label={tRoot('photoViewer.close')}
            className='size-[40px] shrink-0 rounded-full hover:bg-bg2-day active:scale-95'
          >
            <IconX className='size-[22px] text-text-primary-day' />
          </Button>
          <h2 className='truncate text-[18px] font-semibold text-text-primary-day'>{t('filter')}</h2>
        </div>

        {onClear && (
          <Button
            variant='ghost'
            size='sm-compact'
            onClick={onClear}
            className='shrink-0 rounded-[8px] px-[12px] text-[15px] font-medium text-primary-green-day hover:bg-light-green/60'
          >
            {t('clear')}
          </Button>
        )}
      </div>

      <ScrollArea className='min-h-0 flex-1 [&>[data-slot=scroll-area-viewport]>div]:block!'>
        <div className='flex flex-col divide-y divide-primary-stroke-day px-[16px]'>
          <FiltersSection title={t('category_and_parameters')}>
            {isCategoriesLoading ? (
              <InputLikeSelectTrigger isLoading />
            ) : (
              <CategorySelectElem
                openedModal={activeModalId}
                isMobile={true}
                data={categoriesData}
                value={categories}
                showModal={onShowModal}
                closeModal={onCloseModal}
                onChange={onUpdateCategory}
              />
            )}

            {shownParameters.map((param) => {
              const foundParam = params.find((el) => el.id === param.id);
              const paramValue = foundParam?.value;
              const rangeValue = isNumberParamValue(paramValue) ? paramValue : undefined;
              const stringValue = typeof paramValue === 'string' ? paramValue : '';
              const multiValue = Array.isArray(paramValue) ? paramValue : stringValue ? [stringValue] : [];

              const onUpdate = (newVal: string | string[]) => {
                onUpdateParam(param.id, newVal);
              };

              const selectParams = param.enumOptions?.map((val: string) => ({ label: val, value: val })) || [];
              return param.isMultiselectable ? (
                <FiltersSelect
                  key={param.id}
                  triggerClassName='min-w-0 bg-bg2-day! border-transparent'
                  hideLabel={true}
                  openedModal={activeModalId}
                  isMobile={true}
                  showModal={onShowModal}
                  closeModal={onCloseModal}
                  label={param.name || ''}
                  onChange={onUpdate}
                  multiselectMode={true}
                  data={selectParams}
                  value={multiValue}
                />
              ) : (
                <FiltersSelect
                  key={param.id}
                  openedModal={activeModalId}
                  isMobile={true}
                  hideLabel={true}
                  triggerClassName='min-w-0 bg-bg2-day! border-transparent'
                  showModal={onShowModal}
                  closeModal={onCloseModal}
                  label={param.name || ''}
                  multiselectMode={false}
                  onChange={onUpdate}
                  isNumberRange={param.type === 'NUMBER'}
                  numberRangeValue={{ min: rangeValue?.from, max: rangeValue?.to }}
                  onChangeNumberRange={(val) => {
                    onUpdateParam(param.id, { from: val.min, to: val.max });
                  }}
                  data={selectParams}
                  value={stringValue}
                />
              );
            })}
          </FiltersSection>

          <FiltersSection title={t('search_place')}>
            <LocationSelector
              defaultState={{
                regionId: regionId,
                cityId: cityId,
                districtId: districtId,
                countryId: countryId,
              }}
              placeholder={''}
              onChange={(location) => {
                updateLocation(location.countryId ?? '', location.regionId ?? '', location.cityId ?? '', {
                  lat: location.latitude ?? 0,
                  lon: location.longitude ?? 0,
                });
              }}
              GeoShowElement={GeoShow}
            />
          </FiltersSection>

          <FiltersSection title={t('price')}>
            {showFree && <FreePriceToggle checked={isFree} onChange={onChangeIsFree} label={t('free_products')} />}
            {!isFree && <FiltersMinMax value={price} onChange={onChangePrice} />}
          </FiltersSection>

          <FiltersSection>
            <MobileSortSwitch
              openedModal={activeModalId}
              showModal={onShowModal}
              closeModal={onCloseModal}
              selected={sort}
              onChange={onChangeSort}
            />
          </FiltersSection>
        </div>
      </ScrollArea>

      <div className='shrink-0 border-t border-primary-stroke-day bg-white px-[16px] pt-[12px] pb-[calc(12px+env(safe-area-inset-bottom))]'>
        <Button onClick={submitNewSearch} size='full'>
          {t('submit')}
        </Button>
      </div>

      <div
        id='mobile-filters-modal-parent'
        data-modal-root='true'
        className={clsx('absolute inset-0 z-10 bg-white', {
          'hidden!': !activeModalId,
        })}
      />

      {dictsGateState !== 'idle' && (
        <div className='absolute inset-0 z-20 bg-white/75 backdrop-blur-[1px] flex items-center justify-center'>
          {dictsGateState === 'loading' && (
            <div className='flex flex-col items-center gap-[8px] text-text-primary-day'>
              <Spinner className='size-[28px] text-primary-green-day' />
              <span className='text-[14px]'>Loading filters...</span>
            </div>
          )}
          {dictsGateState === 'error' && (
            <div className='text-[14px] px-[16px] text-center text-text-primary-day'>Failed to load filters data.</div>
          )}
        </div>
      )}
    </div>
  );
};

function FiltersSection({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <section className='flex flex-col gap-[12px] py-[20px]'>
      {title && <h3 className='text-[17px] font-semibold text-text-primary-day'>{title}</h3>}
      {children}
    </section>
  );
}
